import { useRef, useState } from 'react'
import { useProjectConfig, useUpdateProjectConfig } from '../../api/hooks'
import { NO_STYLE_ID } from '../../constants/styles'
import { createNadDebug } from '../../utils/debugLog'
import { StyleLibraryModal } from '../canvas-v2/nodes/shared/StyleLibraryModal'
import { QuestionCard, type QuestionOption } from './QuestionCard'

const log = createNadDebug('dw:no-style-onboarding')

/** A separate onboarding question; saving uses the normal Global Config API. */
export function StyleChoiceCard({
  projectId, canvasId, question, options, onAnswer, disabled = false,
}: {
  projectId: string
  canvasId: string
  question: string
  options: QuestionOption[]
  onAnswer: (answer: string) => void
  disabled?: boolean
}) {
  const { data: config } = useProjectConfig(projectId, canvasId)
  const { mutateAsync: updateConfig } = useUpdateProjectConfig()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saveInFlight = useRef(false)

  const saveAndAnswer = async (styleId: string) => {
    if (saveInFlight.current || disabled) return
    saveInFlight.current = true
    setSaving(true)
    setOpen(false)
    setError(null)
    try {
      const saved = await updateConfig({ projectId, canvasId, data: { style_id: styleId } })
      if (saved.style_id !== styleId) throw new Error('The selected style was not saved.')
      log({ event: 'project_style_save_succeeded', project_id: projectId, canvas_id: canvasId, style_id: saved.style_id, save_target: 'global_config', previous_style_id: config?.style_id })
      // Preserve the option identity in the transcript. The Director reads the
      // saved style from its normal live project config context on continuation.
      onAnswer(styleId === NO_STYLE_ID ? 'keep_no_style' : 'select_style')
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause)
      setError(message)
      log({ event: 'style_save_failed', project_id: projectId, style_id: styleId, error: message })
    } finally {
      saveInFlight.current = false
      setSaving(false)
    }
  }

  return (
    <div data-testid="style-choice-card" aria-busy={saving}>
      <QuestionCard
        question={question}
        options={options}
        disabled={disabled || saving || !config}
        onSelect={(value) => {
          if (value === 'select_style') {
            log({ event: 'selector_opened', project_id: projectId, style_id: config?.style_id, trigger: 'select_style_action' })
            setOpen(true)
          } else if (value === 'keep_no_style') {
            void saveAndAnswer(NO_STYLE_ID)
          } else {
            throw new Error(`Unknown style choice: ${value}`)
          }
        }}
      />
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
      {open && config && (
        <StyleLibraryModal
          isOpen
          currentStyleId={config.style_id}
          onClose={() => {
            log({ event: 'selector_dismissed', project_id: projectId, dismiss_reason: 'closed_without_selection' })
            setOpen(false)
          }}
          onApply={(styleId) => { void saveAndAnswer(styleId) }}
        />
      )}
    </div>
  )
}
